import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatIqd, fromMillidinars, type CustomerOrderHistoryRow } from '@gc/shared';
import type { RefundOrderResultDto } from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import { Shell } from './RefillDialog';

/**
 * Profile → Order History → "Refund Order" (owner, 2026-08-12): give a paid
 * order back to the wallet — the whole thing, or item by item ("if an order
 * has 1xPepsi and 1xBurger i want to also be able to only refund one of them
 * or both").
 *
 * Every line starts selected at its full REMAINING quantity — the common case
 * is "the whole order back", and unticking is cheaper than building a
 * selection from nothing. What has already been refunded is simply not
 * offered; the server enforces the same cap, so a stale drawer refuses
 * honestly rather than double-paying.
 *
 * The preview totals the MENU prices; the server refunds at the rate the bill
 * was actually settled at (a discounted checkout refunds the discounted
 * price, a service-charged one gives the service back with the item), so the
 * done screen shows the server's own figure.
 */
export function RefundOrderDialog({
  customerId,
  order,
  currencySymbol,
  onClose,
  onDone,
}: {
  customerId: string;
  order: CustomerOrderHistoryRow;
  currencySymbol: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const money = (md: string | bigint) =>
    formatIqd(fromMillidinars(typeof md === 'bigint' ? md.toString() : md), { currencySymbol });

  const refundable = useMemo(
    () =>
      order.items
        .map((item) => ({ ...item, remaining: item.quantity - item.refundedQuantity }))
        .filter((item) => item.remaining > 0),
    [order],
  );

  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    Object.fromEntries(refundable.map((item) => [item.orderItemId, item.remaining])),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RefundOrderResultDto | null>(null);

  const previewMd = refundable.reduce(
    (total, item) =>
      total + BigInt(item.unitPriceMd) * BigInt(quantities[item.orderItemId] ?? 0),
    0n,
  );

  const setQuantity = (orderItemId: string, next: number, max: number) =>
    setQuantities((current) => ({
      ...current,
      [orderItemId]: Math.max(0, Math.min(max, next)),
    }));

  async function submit() {
    const items = refundable
      .map((item) => ({ orderItemId: item.orderItemId, quantity: quantities[item.orderItemId] ?? 0 }))
      .filter((item) => item.quantity > 0);
    if (items.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const refunded = await api.post<RefundOrderResultDto>(
        `/customers/${customerId}/orders/${order.orderId}/refund`,
        { items },
      );
      setResult(refunded);
      onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('customers.orderRefund.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <Shell label={t('customers.orderRefund.done')} onClose={onClose}>
        <h2 className="text-lg font-semibold text-content">{t('customers.orderRefund.done')}</h2>
        <dl className="tabular mt-4 rounded-card bg-raised p-3 text-sm">
          <div className="flex justify-between py-1">
            <dt className="text-content-secondary">{t('customers.orderRefund.amount')}</dt>
            <dd className="font-semibold text-content">{money(result.refundedMd)}</dd>
          </div>
          <div className="flex justify-between py-1">
            <dt className="text-content-secondary">{t('customers.refill.newBalance')}</dt>
            <dd className="text-content">{money(result.balanceAfterMd)}</dd>
          </div>
        </dl>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-fg hover:bg-primary-hover"
        >
          {t('common.confirm')}
        </button>
      </Shell>
    );
  }

  return (
    <Shell label={t('customers.orderRefund.title')} onClose={onClose}>
      <h2 className="text-lg font-semibold text-content">
        {t('customers.orderRefund.title')}
        {order.invoiceNumber ? ` — ${order.invoiceNumber}` : ''}
      </h2>

      <ul className="mt-4 space-y-2">
        {refundable.map((item) => {
          const chosen = quantities[item.orderItemId] ?? 0;
          return (
            <li
              key={item.orderItemId}
              className="flex items-center justify-between gap-3 rounded-card bg-raised px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-content">{item.name}</p>
                <p className="tabular text-xs text-content-muted">
                  {money(item.unitPriceMd)}
                  {item.refundedQuantity > 0
                    ? ` · ${t('customers.orderRefund.alreadyRefunded', {
                        count: item.refundedQuantity,
                      })}`
                    : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  aria-label={`- ${item.name}`}
                  onClick={() => setQuantity(item.orderItemId, chosen - 1, item.remaining)}
                  className="grid h-8 w-8 place-items-center rounded-full border border-subtle text-content-secondary hover:bg-hover"
                >
                  −
                </button>
                <span className="tabular w-6 text-center text-sm text-content">{chosen}</span>
                <button
                  type="button"
                  aria-label={`+ ${item.name}`}
                  onClick={() => setQuantity(item.orderItemId, chosen + 1, item.remaining)}
                  className="grid h-8 w-8 place-items-center rounded-full border border-subtle text-content-secondary hover:bg-hover"
                >
                  +
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="tabular mt-4 flex items-center justify-between rounded-card bg-raised px-3 py-2 text-sm">
        <span className="text-content-secondary">{t('customers.orderRefund.amount')}</span>
        <span className="font-semibold text-content">{money(previewMd)}</span>
      </div>

      {error ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={submitting || previewMd <= 0n}
          className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
        >
          {submitting ? t('common.loading') : t('customers.orderRefund.title')}
        </button>
      </div>
    </Shell>
  );
}
